import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Settings,
  Coffee,
  UtensilsCrossed,
  Clock,
  Bell,
  Save,
  TrendingUp,
  AlertTriangle,
  UserX,
  Users,
  User,
  Shield,
  Camera,
  Eye,
  ArrowLeftRight,
  CalendarDays,
  Calendar,
  Timer,
  Gauge,
  LogOut,
  DollarSign,
  Mail,
  X,
  Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEK_DAYS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSection, setSavingSection] = useState(null);
  const [breakSettings, setBreakSettings] = useState({
    breakfast_duration_minutes: 30,
    lunch_duration_minutes: 60,
    break_warning_enabled: true,
    break_warning_threshold_minutes: 60
  });
  const [overtimeSettings, setOvertimeSettings] = useState({
    period_type: 'fortnightly',
    standard_weekly_hours: 38.0,
    standard_fortnight_hours: 76.0,
    week_start_day: 0,
    period_reference_date: '',
    overtime_alert_enabled: true,
    overtime_alert_threshold_percent: 90,
    send_overtime_email_alerts: true,
    notify_employee_overtime: true,
    show_period_progress_to_employees: true
  });
  const [lateClockInSettings, setLateClockInSettings] = useState({
    enabled: true,
    threshold_minutes: 15,
    notify_employee: true,
    notify_admin: true,
    notify_managers: true,
    email_employee: false,
    email_admin: true,
    email_managers: false
  });
  const [facialRecognitionSettings, setFacialRecognitionSettings] = useState({
    enabled: false,
    enforcement_mode: 'warn',
    liveness_check_enabled: false,
    enrollment_required: false
  });
  const [shiftSwapSettings, setShiftSwapSettings] = useState({
    enabled: true,
    swap_with_any: true,
    require_manager_approval: true,
    require_coworker_approval: true,
    max_swap_days_ahead: 30
  });
  const [rosterSettings, setRosterSettings] = useState({
    incomplete_roster_notification: true,
    notification_day: 'sunday',
    notification_time: '18:00',
    notify_admins: true,
    notify_managers: true
  });
  const [clockOutSettings, setClockOutSettings] = useState({
    enabled: false,
    reminder_time: '18:00',
    notify_employee: true,
    notify_admin: true,
    notify_managers: true,
    email_employee: false,
    email_admin: true,
    email_managers: false
  });
  const [payrollSettings, setPayrollSettings] = useState({
    enabled: false,
    frequency: 'fortnightly',
    reference_date: '',
    last_pay_day: 'sunday',
    next_payroll_end_date: null,
    last_alert_sent_date: null
  });
  const [emailPreview, setEmailPreview] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [rosterEmailPreview, setRosterEmailPreview] = useState(null);
  const [showRosterEmailPreview, setShowRosterEmailPreview] = useState(false);
  const [adminOnRoster, setAdminOnRoster] = useState(false);
  const [savingAdminRoster, setSavingAdminRoster] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setAdminOnRoster(response.data.on_roster || false);
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
    }
  };

  const handleToggleAdminRoster = async () => {
    setSavingAdminRoster(true);
    try {
      const newValue = !adminOnRoster;
      await axios.put(`${API}/auth/me/roster-toggle`, { on_roster: newValue });
      setAdminOnRoster(newValue);
      toast.success(newValue ? 'You are now visible on the roster' : 'You are now hidden from the roster');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update roster visibility');
    } finally {
      setSavingAdminRoster(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      if (response.data?.break_settings) {
        setBreakSettings(response.data.break_settings);
      }
      if (response.data?.overtime_settings) {
        setOvertimeSettings(response.data.overtime_settings);
      }
      if (response.data?.late_clockin_settings) {
        setLateClockInSettings(response.data.late_clockin_settings);
      }
      if (response.data?.facial_recognition_settings) {
        setFacialRecognitionSettings(response.data.facial_recognition_settings);
      }
      if (response.data?.shift_swap_settings) {
        setShiftSwapSettings(response.data.shift_swap_settings);
      }
      if (response.data?.roster_settings) {
        setRosterSettings(response.data.roster_settings);
      }
      if (response.data?.clock_out_reminder_settings) {
        setClockOutSettings(response.data.clock_out_reminder_settings);
      }
      if (response.data?.payroll_settings) {
        setPayrollSettings(response.data.payroll_settings);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBreaks = async () => {
    setSavingSection('breaks');
    try {
      await axios.put(`${API}/settings/company/breaks`, breakSettings);
      toast.success('Break settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveOvertime = async () => {
    setSavingSection('overtime');
    try {
      await axios.put(`${API}/settings/company/overtime`, overtimeSettings);
      toast.success('Overtime settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveFacialRecognition = async () => {
    setSavingSection('facial');
    try {
      await axios.put(`${API}/settings/company/facial-recognition`, facialRecognitionSettings);
      toast.success('Facial recognition settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSection(null);
    }
  };

  const updateBreakSetting = (key, value) => {
    setBreakSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateOvertimeSetting = (key, value) => {
    setOvertimeSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateLateClockInSetting = (key, value) => {
    setLateClockInSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateShiftSwapSetting = (key, value) => {
    setShiftSwapSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveLateClockIn = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/company/late-clockin`, lateClockInSettings);
      toast.success('Late clock-in settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveShiftSwap = async () => {
    setSavingSection('shiftSwap');
    try {
      await axios.put(`${API}/settings/company/shift-swap`, shiftSwapSettings);
      toast.success('Shift swap settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveRoster = async () => {
    setSavingSection('roster');
    try {
      await axios.put(`${API}/settings/company/roster`, rosterSettings);
      toast.success('Roster notification settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveClockOut = async () => {
    setSavingSection('clockOut');
    try {
      await axios.put(`${API}/settings/company/clock-out-reminder`, clockOutSettings);
      toast.success('Clock-out reminder settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSavePayroll = async () => {
    setSavingSection('payroll');
    try {
      const response = await axios.put(`${API}/settings/payroll`, payrollSettings);
      if (response.data?.payroll_settings) {
        setPayrollSettings(response.data.payroll_settings);
      }
      toast.success('Payroll reminder settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSection(null);
    }
  };

  const handlePreviewEmail = async () => {
    setLoadingPreview(true);
    try {
      const response = await axios.get(`${API}/settings/payroll/preview-email`);
      setEmailPreview(response.data);
      setShowEmailPreview(true);
    } catch (error) {
      console.error('Failed to load email preview:', error);
      toast.error(error.response?.data?.detail || 'Failed to load email preview. Make sure payroll settings are configured.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const updatePayrollSetting = (key, value) => {
    setPayrollSettings(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    // dd/MM/yyyy — NZ convention
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 w-full overflow-x-hidden">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-8 pb-20 md:pb-8 overflow-x-hidden w-full">
        <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[hsl(243,65%,20%)] flex items-center justify-center text-white shrink-0">
              <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">Company Settings</h1>
              <p className="text-sm sm:text-base text-slate-500">Configure break times, overtime, and notifications</p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Email Configuration Test */}
              <Card className="border-0 shadow-lg bg-gradient-to-r from-indigo-50 to-purple-50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-medium font-['Manrope']">Email Notifications</h3>
                        <p className="text-sm text-slate-500">Test your email configuration</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/settings/test-email`);
                          toast.success(res.data.message);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to send test email');
                        }
                      }}
                      className="bg-white hover:bg-indigo-50"
                      data-testid="test-email-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Test Email
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Break Duration Settings */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[hsl(243,65%,20%)]" />
                    Standard Break Durations
                  </CardTitle>
                  <CardDescription>
                    Set the expected duration for breakfast and lunch breaks.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Breakfast Duration */}
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Coffee className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Breakfast Break</Label>
                        <p className="text-sm text-slate-500">Standard morning break duration</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="5"
                        max="120"
                        value={breakSettings.breakfast_duration_minutes}
                        onChange={(e) => updateBreakSetting('breakfast_duration_minutes', parseInt(e.target.value) || 30)}
                        className="w-20 text-center"
                        data-testid="breakfast-duration-input"
                      />
                      <span className="text-sm text-slate-500">minutes</span>
                    </div>
                  </div>

                  {/* Lunch Duration */}
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <UtensilsCrossed className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Lunch Break</Label>
                        <p className="text-sm text-slate-500">Standard midday break duration</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="15"
                        max="180"
                        value={breakSettings.lunch_duration_minutes}
                        onChange={(e) => updateBreakSetting('lunch_duration_minutes', parseInt(e.target.value) || 60)}
                        className="w-20 text-center"
                        data-testid="lunch-duration-input"
                      />
                      <span className="text-sm text-slate-500">minutes</span>
                    </div>
                  </div>

                  {/* Warning Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Enable Break Warnings</Label>
                        <p className="text-sm text-slate-500">
                          Notify employees when their break exceeds the threshold
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={breakSettings.break_warning_enabled}
                      onCheckedChange={(checked) => updateBreakSetting('break_warning_enabled', checked)}
                      data-testid="break-warning-toggle"
                    />
                  </div>

                  {/* Warning Threshold */}
                  {breakSettings.break_warning_enabled && (
                    <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Warning Threshold</Label>
                          <p className="text-sm text-slate-500">
                            Alert after this many minutes on any break
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="15"
                          max="180"
                          value={breakSettings.break_warning_threshold_minutes}
                          onChange={(e) => updateBreakSetting('break_warning_threshold_minutes', parseInt(e.target.value) || 60)}
                          className="w-20 text-center"
                          data-testid="warning-threshold-input"
                        />
                        <span className="text-sm text-slate-500">minutes</span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button 
                      onClick={handleSaveBreaks}
                      disabled={saving}
                      className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)] gap-2"
                      data-testid="save-breaks-btn"
                    >
                      {saving ? <div className="spinner" /> : <Save className="w-4 h-4" />}
                      Save Break Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Overtime Settings */}
              <Card className="border-0 shadow-lg" data-testid="overtime-settings-card">
                <CardHeader>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[hsl(243,65%,20%)]" />
                    Overtime Tracking
                  </CardTitle>
                  <CardDescription>
                    Configure pay periods, standard hours, and overtime alerts.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Period Type */}
                  <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Pay Period Type</Label>
                        <p className="text-sm text-slate-500">
                          Track hours by week or fortnight
                        </p>
                      </div>
                    </div>
                    <Select
                      value={overtimeSettings.period_type}
                      onValueChange={(value) => updateOvertimeSetting('period_type', value)}
                    >
                      <SelectTrigger className="w-40" data-testid="period-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Standard Hours - Dynamic based on period type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Timer className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Weekly Hours</Label>
                          <p className="text-sm text-slate-500">Standard hours/week</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          step="0.5"
                          value={overtimeSettings.standard_weekly_hours}
                          onChange={(e) => updateOvertimeSetting('standard_weekly_hours', parseFloat(e.target.value) || 38)}
                          className="w-20 text-center"
                          data-testid="weekly-hours-input"
                        />
                        <span className="text-xs text-slate-500">hrs</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Timer className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Fortnight Hours</Label>
                          <p className="text-sm text-slate-500">Standard hours/fortnight</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="200"
                          step="0.5"
                          value={overtimeSettings.standard_fortnight_hours}
                          onChange={(e) => updateOvertimeSetting('standard_fortnight_hours', parseFloat(e.target.value) || 76)}
                          className="w-20 text-center"
                          data-testid="fortnight-hours-input"
                        />
                        <span className="text-xs text-slate-500">hrs</span>
                      </div>
                    </div>
                  </div>

                  {/* Week Start Day */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <CalendarDays className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Week Starts On</Label>
                        <p className="text-sm text-slate-500">
                          First day of the work week
                        </p>
                      </div>
                    </div>
                    <Select
                      value={String(overtimeSettings.week_start_day)}
                      onValueChange={(value) => updateOvertimeSetting('week_start_day', parseInt(value))}
                    >
                      <SelectTrigger className="w-36" data-testid="week-start-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEK_DAYS.map(day => (
                          <SelectItem key={day.value} value={String(day.value)}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Period Reference Date (for Fortnightly) */}
                  {overtimeSettings.period_type === 'fortnightly' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Fortnight Reference Date</Label>
                            <p className="text-sm text-slate-500">
                              Set this once - fortnights will auto-calculate from this date
                            </p>
                          </div>
                        </div>
                        <Input
                          type="date"
                          value={overtimeSettings.period_reference_date || ''}
                          onChange={(e) => updateOvertimeSetting('period_reference_date', e.target.value)}
                          className="w-40"
                          data-testid="reference-date-input"
                        />
                      </div>
                      {overtimeSettings.period_reference_date && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-700">
                            <span className="font-medium">✓ Auto-tracking enabled:</span> Fortnights automatically roll over every 14 days from your reference date. No manual updates needed.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Overtime Alert Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Enable Overtime Alerts</Label>
                        <p className="text-sm text-slate-500">
                          Get notified when employees approach overtime
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={overtimeSettings.overtime_alert_enabled}
                      onCheckedChange={(checked) => updateOvertimeSetting('overtime_alert_enabled', checked)}
                      data-testid="overtime-alert-toggle"
                    />
                  </div>

                  {/* Alert Threshold */}
                  {overtimeSettings.overtime_alert_enabled && (
                    <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <Gauge className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Alert Threshold</Label>
                          <p className="text-sm text-slate-500">
                            Alert when reaching this % of standard hours
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="50"
                          max="100"
                          value={overtimeSettings.overtime_alert_threshold_percent}
                          onChange={(e) => updateOvertimeSetting('overtime_alert_threshold_percent', parseInt(e.target.value) || 90)}
                          className="w-20 text-center"
                          data-testid="alert-threshold-input"
                        />
                        <span className="text-sm text-slate-500">%</span>
                      </div>
                    </div>
                  )}

                  {/* Email Alerts Toggle */}
                  {overtimeSettings.overtime_alert_enabled && (
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Bell className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Send Email Alerts</Label>
                          <p className="text-sm text-slate-500">
                            Email admins and managers when overtime is projected
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={overtimeSettings.send_overtime_email_alerts}
                        onCheckedChange={(checked) => updateOvertimeSetting('send_overtime_email_alerts', checked)}
                        data-testid="email-alerts-toggle"
                      />
                    </div>
                  )}

                  {/* Notify Employee Toggle */}
                  {overtimeSettings.overtime_alert_enabled && (
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                          <Users className="w-5 h-5 text-cyan-600" />
                        </div>
                        <div>
                          <Label className="font-medium">Notify Employees</Label>
                          <p className="text-sm text-slate-500">
                            Also notify the employee about their projected overtime
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={overtimeSettings.notify_employee_overtime}
                        onCheckedChange={(checked) => updateOvertimeSetting('notify_employee_overtime', checked)}
                        data-testid="notify-employee-toggle"
                      />
                    </div>
                  )}

                  {/* Employee Visibility Toggle */}
                  <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <Eye className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Show Period Progress to Employees</Label>
                        <p className="text-sm text-slate-500">
                          Allow employees to see their period progress on their dashboard
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={overtimeSettings.show_period_progress_to_employees}
                      onCheckedChange={(checked) => updateOvertimeSetting('show_period_progress_to_employees', checked)}
                      data-testid="employee-visibility-toggle"
                    />
                  </div>

                  {/* Summary Box */}
                  <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                    <p className="text-sm font-medium text-indigo-900 mb-2">Current Configuration</p>
                    <div className="grid grid-cols-2 gap-2 text-sm text-indigo-700">
                      <div>Period: <span className="font-medium capitalize">{overtimeSettings.period_type}</span></div>
                      <div>Standard Hours: <span className="font-medium">
                        {overtimeSettings.period_type === 'weekly' 
                          ? `${overtimeSettings.standard_weekly_hours}hrs/week`
                          : `${overtimeSettings.standard_fortnight_hours}hrs/fortnight`
                        }
                      </span></div>
                      <div>Week starts: <span className="font-medium">{WEEK_DAYS.find(d => d.value === overtimeSettings.week_start_day)?.label}</span></div>
                      <div>Alert at: <span className="font-medium">{overtimeSettings.overtime_alert_threshold_percent}%</span></div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button 
                      onClick={handleSaveOvertime}
                      disabled={saving}
                      className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)] gap-2"
                      data-testid="save-overtime-btn"
                    >
                      {saving ? <div className="spinner" /> : <Save className="w-4 h-4" />}
                      Save Overtime Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Late Clock-in Notification Settings */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <UserX className="w-5 h-5 text-[hsl(243,65%,20%)]" />
                    Late Clock-in Alerts
                  </CardTitle>
                  <CardDescription>
                    Get notified when rostered employees don't clock in on time.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Late Clock-in Alerts */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Enable Late Clock-in Alerts</Label>
                        <p className="text-sm text-slate-500">
                          Notify when employees miss their scheduled clock-in time
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={lateClockInSettings.enabled}
                      onCheckedChange={(checked) => updateLateClockInSetting('enabled', checked)}
                      data-testid="late-clockin-toggle"
                    />
                  </div>

                  {lateClockInSettings.enabled && (
                    <>
                      {/* Threshold Minutes */}
                      <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Alert Threshold</Label>
                            <p className="text-sm text-slate-500">
                              Minutes after scheduled start to trigger alert
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="5"
                            max="120"
                            value={lateClockInSettings.threshold_minutes}
                            onChange={(e) => updateLateClockInSetting('threshold_minutes', parseInt(e.target.value) || 15)}
                            className="w-20 text-center"
                            data-testid="late-threshold-input"
                          />
                          <span className="text-sm text-slate-500">minutes</span>
                        </div>
                      </div>

                      {/* Notify Admin */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Notify Admins</Label>
                            <p className="text-sm text-slate-500">
                              Send notifications to company administrators
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={lateClockInSettings.notify_admin}
                          onCheckedChange={(checked) => updateLateClockInSetting('notify_admin', checked)}
                          data-testid="notify-admin-toggle"
                        />
                      </div>

                      {/* Notify Managers */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Notify Managers</Label>
                            <p className="text-sm text-slate-500">
                              Send notifications to team managers
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={lateClockInSettings.notify_managers}
                          onCheckedChange={(checked) => updateLateClockInSetting('notify_managers', checked)}
                          data-testid="notify-managers-toggle"
                        />
                      </div>

                      {/* Notify Employee */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Notify Employee</Label>
                            <p className="text-sm text-slate-500">
                              Send notification to the late employee themselves
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={lateClockInSettings.notify_employee}
                          onCheckedChange={(checked) => updateLateClockInSetting('notify_employee', checked)}
                          data-testid="notify-employee-toggle"
                        />
                      </div>

                      {/* Email Notifications Section */}
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center gap-2 mb-4">
                          <Mail className="w-5 h-5 text-slate-600" />
                          <Label className="font-semibold">Email Notifications</Label>
                          <span className="text-xs text-slate-500">(Optional)</span>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                            <div>
                              <Label className="text-sm font-medium">Email Admin</Label>
                            </div>
                            <Switch
                              checked={lateClockInSettings.email_admin}
                              onCheckedChange={(checked) => updateLateClockInSetting('email_admin', checked)}
                              data-testid="email-admin-toggle"
                            />
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                            <div>
                              <Label className="text-sm font-medium">Email Managers</Label>
                            </div>
                            <Switch
                              checked={lateClockInSettings.email_managers}
                              onCheckedChange={(checked) => updateLateClockInSetting('email_managers', checked)}
                              data-testid="email-managers-toggle"
                            />
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                            <div>
                              <Label className="text-sm font-medium">Email Employee</Label>
                            </div>
                            <Switch
                              checked={lateClockInSettings.email_employee}
                              onCheckedChange={(checked) => updateLateClockInSetting('email_employee', checked)}
                              data-testid="email-employee-late-toggle"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-end">
                    <Button 
                      onClick={handleSaveLateClockIn}
                      disabled={savingSection === 'lateClockin'}
                      className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)] gap-2"
                      data-testid="save-late-clockin-btn"
                    >
                      {savingSection === 'lateClockin' ? <div className="spinner" /> : <Save className="w-4 h-4" />}
                      Save Late Clock-in Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Clock-Out Reminder Settings */}
              <Card className="border-0 shadow-lg" data-testid="clock-out-reminder-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                      <LogOut className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle className="font-['Manrope']">Clock-Out Reminder</CardTitle>
                      <CardDescription>Remind employees to clock out at a set time</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="font-medium">Enable Clock-Out Reminders</p>
                        <p className="text-sm text-slate-500">Send reminders when employees haven't clocked out</p>
                      </div>
                    </div>
                    <Switch
                      checked={clockOutSettings.enabled}
                      onCheckedChange={(checked) => setClockOutSettings(prev => ({...prev, enabled: checked}))}
                      data-testid="clock-out-reminder-toggle"
                    />
                  </div>

                  {clockOutSettings.enabled && (
                    <div className="space-y-4 pl-4 border-l-2 border-orange-200">
                      {/* Reminder Time */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Reminder Time</Label>
                        <Input
                          type="time"
                          value={clockOutSettings.reminder_time}
                          onChange={(e) => setClockOutSettings(prev => ({...prev, reminder_time: e.target.value}))}
                          className="w-40"
                          data-testid="clock-out-reminder-time"
                        />
                        <p className="text-xs text-slate-500">Time to send clock-out reminders (24-hour format)</p>
                      </div>

                      {/* Who to Notify */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Who to Notify</Label>
                        
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-600" />
                            <span className="text-sm">Employees (who haven't clocked out)</span>
                          </div>
                          <Switch
                            checked={clockOutSettings.notify_employee}
                            onCheckedChange={(checked) => setClockOutSettings(prev => ({...prev, notify_employee: checked}))}
                            data-testid="clock-out-notify-employee"
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-slate-600" />
                            <span className="text-sm">Company Admins</span>
                          </div>
                          <Switch
                            checked={clockOutSettings.notify_admin}
                            onCheckedChange={(checked) => setClockOutSettings(prev => ({...prev, notify_admin: checked}))}
                            data-testid="clock-out-notify-admin"
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-600" />
                            <span className="text-sm">Managers</span>
                          </div>
                          <Switch
                            checked={clockOutSettings.notify_managers}
                            onCheckedChange={(checked) => setClockOutSettings(prev => ({...prev, notify_managers: checked}))}
                            data-testid="clock-out-notify-managers"
                          />
                        </div>
                      </div>

                      {/* Email Notifications Section */}
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center gap-2 mb-4">
                          <Mail className="w-5 h-5 text-slate-600" />
                          <Label className="font-semibold">Email Notifications</Label>
                          <span className="text-xs text-slate-500">(Optional)</span>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                            <div>
                              <Label className="text-sm font-medium">Email Admin</Label>
                            </div>
                            <Switch
                              checked={clockOutSettings.email_admin}
                              onCheckedChange={(checked) => setClockOutSettings(prev => ({...prev, email_admin: checked}))}
                              data-testid="clock-out-email-admin"
                            />
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                            <div>
                              <Label className="text-sm font-medium">Email Managers</Label>
                            </div>
                            <Switch
                              checked={clockOutSettings.email_managers}
                              onCheckedChange={(checked) => setClockOutSettings(prev => ({...prev, email_managers: checked}))}
                              data-testid="clock-out-email-managers"
                            />
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                            <div>
                              <Label className="text-sm font-medium">Email Employee</Label>
                            </div>
                            <Switch
                              checked={clockOutSettings.email_employee}
                              onCheckedChange={(checked) => setClockOutSettings(prev => ({...prev, email_employee: checked}))}
                              data-testid="clock-out-email-employee"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Info Box */}
                      <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-700">
                        <p><strong>How it works:</strong> At {clockOutSettings.reminder_time || '18:00'}, the system checks for employees who are still clocked in and sends reminders to help them remember to clock out.</p>
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={handleSaveClockOut}
                    className="w-full bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                    disabled={savingSection === 'clockOut'}
                    data-testid="save-clock-out-btn"
                  >
                    {savingSection === 'clockOut' ? (
                      <div className="spinner" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Clock-Out Settings
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Payroll Reminder Settings */}
              <Card className="border-0 shadow-lg" data-testid="payroll-settings-card">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CalendarDays className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="font-['Manrope']">Payroll Reminders</CardTitle>
                      <CardDescription>
                        Get notified when it's time to process payroll
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium">Enable Payroll Reminders</p>
                        <p className="text-sm text-slate-500">
                          Send email reminders on the last day of each pay period
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={payrollSettings.enabled}
                      onCheckedChange={(checked) => updatePayrollSetting('enabled', checked)}
                      data-testid="payroll-enable-toggle"
                    />
                  </div>

                  {payrollSettings.enabled && (
                    <div className="space-y-4 pt-2">
                      {/* Payroll Frequency */}
                      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                        <div>
                          <Label className="text-sm font-medium">Payroll Frequency</Label>
                          <p className="text-xs text-slate-500 mb-2">How often do you process payroll?</p>
                          <Select
                            value={payrollSettings.frequency}
                            onValueChange={(value) => updatePayrollSetting('frequency', value)}
                          >
                            <SelectTrigger className="w-full" data-testid="payroll-frequency">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="weekly">Weekly (7 days)</SelectItem>
                              <SelectItem value="fortnightly">Fortnightly (14 days)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-sm font-medium">Reference Date</Label>
                          <p className="text-xs text-slate-500 mb-2">Start of first payroll cycle</p>
                          <Input
                            type="date"
                            value={payrollSettings.reference_date || ''}
                            onChange={(e) => updatePayrollSetting('reference_date', e.target.value)}
                            className="w-full"
                            data-testid="payroll-reference-date"
                          />
                        </div>

                        <div>
                          <Label className="text-sm font-medium">Last Day of Pay Period</Label>
                          <p className="text-xs text-slate-500 mb-2">Which day does the pay period end?</p>
                          <Select
                            value={payrollSettings.last_pay_day}
                            onValueChange={(value) => updatePayrollSetting('last_pay_day', value)}
                          >
                            <SelectTrigger className="w-full" data-testid="payroll-last-day">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="monday">Monday</SelectItem>
                              <SelectItem value="tuesday">Tuesday</SelectItem>
                              <SelectItem value="wednesday">Wednesday</SelectItem>
                              <SelectItem value="thursday">Thursday</SelectItem>
                              <SelectItem value="friday">Friday</SelectItem>
                              <SelectItem value="saturday">Saturday</SelectItem>
                              <SelectItem value="sunday">Sunday</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Next Payroll Date Info */}
                      {payrollSettings.next_payroll_end_date && (
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                          <div className="flex items-center gap-2 text-emerald-700">
                            <Calendar className="w-4 h-4" />
                            <span className="font-medium">Next Payroll End Date</span>
                          </div>
                          <p className="text-emerald-900 font-semibold mt-1">
                            {formatDate(payrollSettings.next_payroll_end_date)}
                          </p>
                          <p className="text-xs text-emerald-600 mt-1">
                            You'll receive an email reminder at 9:00 AM on this date
                          </p>
                        </div>
                      )}

                      {/* Info Box */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start gap-2">
                          <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">How it works:</p>
                            <ul className="list-disc list-inside space-y-1 text-xs">
                              <li>System calculates payroll cycles from your reference date</li>
                              <li>Email is sent at 9:00 AM on the last day of each pay period</li>
                              <li>Email includes period dates and link to reports</li>
                              <li>Only one email is sent per cycle</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleSavePayroll}
                      disabled={savingSection === 'payroll'}
                      data-testid="save-payroll-btn"
                    >
                      {savingSection === 'payroll' ? (
                        <div className="spinner" />
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Settings
                        </>
                      )}
                    </Button>
                    {payrollSettings.enabled && payrollSettings.reference_date && (
                      <Button
                        variant="outline"
                        className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={handlePreviewEmail}
                        disabled={loadingPreview}
                        data-testid="preview-email-btn"
                      >
                        {loadingPreview ? (
                          <div className="spinner" />
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-2" />
                            Preview Email
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Facial Recognition Settings */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <Camera className="w-5 h-5 text-[hsl(243,65%,20%)]" />
                    Facial Recognition
                  </CardTitle>
                  <CardDescription>
                    Configure face verification settings for employee clock-ins
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Camera className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Enable Facial Recognition</Label>
                        <p className="text-sm text-slate-500">
                          Require face verification during clock-in
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={facialRecognitionSettings.enabled}
                      onCheckedChange={(checked) => 
                        setFacialRecognitionSettings(prev => ({ ...prev, enabled: checked }))
                      }
                      data-testid="facial-recognition-toggle"
                    />
                  </div>

                  {facialRecognitionSettings.enabled && (
                    <>
                      {/* Enforcement Mode */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Enforcement Mode</Label>
                            <p className="text-sm text-slate-500">
                              Choose how to handle failed verifications
                            </p>
                          </div>
                        </div>
                        <Select
                          value={facialRecognitionSettings.enforcement_mode}
                          onValueChange={(value) => 
                            setFacialRecognitionSettings(prev => ({ ...prev, enforcement_mode: value }))
                          }
                        >
                          <SelectTrigger className="w-32" data-testid="enforcement-mode-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="warn">Warn Only</SelectItem>
                            <SelectItem value="block">Block Clock-in</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Liveness Check */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <Eye className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Liveness Check</Label>
                            <p className="text-sm text-slate-500">
                              Require blink detection to prevent photos
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={facialRecognitionSettings.liveness_check_enabled}
                          onCheckedChange={(checked) => 
                            setFacialRecognitionSettings(prev => ({ ...prev, liveness_check_enabled: checked }))
                          }
                          data-testid="liveness-check-toggle"
                        />
                      </div>

                      {/* Enrollment Required */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Enrollment Required</Label>
                            <p className="text-sm text-slate-500">
                              Block clock-in until employee enrolls face
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={facialRecognitionSettings.enrollment_required}
                          onCheckedChange={(checked) => 
                            setFacialRecognitionSettings(prev => ({ ...prev, enrollment_required: checked }))
                          }
                          data-testid="enrollment-required-toggle"
                        />
                      </div>
                    </>
                  )}

                  <div className="flex justify-end">
                    <Button 
                      onClick={handleSaveFacialRecognition}
                      disabled={savingSection === 'facial'}
                      className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)] gap-2"
                      data-testid="save-facial-recognition-btn"
                    >
                      {savingSection === 'facial' ? <div className="spinner" /> : <Save className="w-4 h-4" />}
                      Save Facial Recognition Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Shift Swap Settings Card */}
              <Card className="border-0 shadow-lg" data-testid="shift-swap-settings-card">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                      <ArrowLeftRight className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="font-['Manrope']">Shift Swap Settings</CardTitle>
                      <CardDescription>Configure how employees can swap shifts</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Shift Swaps */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <ArrowLeftRight className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <Label className="font-medium">Enable Shift Swaps</Label>
                        <p className="text-sm text-slate-500">
                          Allow employees to request shift swaps with each other
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={shiftSwapSettings.enabled}
                      onCheckedChange={(checked) => updateShiftSwapSetting('enabled', checked)}
                      data-testid="shift-swap-toggle"
                    />
                  </div>

                  {shiftSwapSettings.enabled && (
                    <>
                      {/* Swap with Anyone */}
                      <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Swap with Anyone</Label>
                            <p className="text-sm text-slate-500">
                              Allow swapping shifts with any eligible employee
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={shiftSwapSettings.swap_with_any}
                          onCheckedChange={(checked) => updateShiftSwapSetting('swap_with_any', checked)}
                          data-testid="swap-with-any-toggle"
                        />
                      </div>

                      {/* Require Manager Approval */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Require Manager Approval</Label>
                            <p className="text-sm text-slate-500">
                              Manager must approve swaps after employees agree
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={shiftSwapSettings.require_manager_approval}
                          onCheckedChange={(checked) => updateShiftSwapSetting('require_manager_approval', checked)}
                          data-testid="require-manager-approval-toggle"
                        />
                      </div>

                      {/* Require Coworker Approval */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Require Coworker Acceptance</Label>
                            <p className="text-sm text-slate-500">
                              The other employee must accept the swap request
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={shiftSwapSettings.require_coworker_approval}
                          onCheckedChange={(checked) => updateShiftSwapSetting('require_coworker_approval', checked)}
                          data-testid="require-coworker-approval-toggle"
                        />
                      </div>

                      {/* Max Days Ahead */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <CalendarDays className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Max Days Ahead</Label>
                            <p className="text-sm text-slate-500">
                              How far in advance swaps can be requested
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="90"
                            value={shiftSwapSettings.max_swap_days_ahead}
                            onChange={(e) => updateShiftSwapSetting('max_swap_days_ahead', parseInt(e.target.value) || 30)}
                            className="w-20 text-center"
                            data-testid="max-swap-days-input"
                          />
                          <span className="text-sm text-slate-500">days</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Save Button */}
                  <Button 
                    onClick={handleSaveShiftSwap}
                    disabled={savingSection === 'shiftSwap'}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    data-testid="save-shift-swap-btn"
                  >
                    {savingSection === 'shiftSwap' ? (
                      <div className="spinner" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Shift Swap Settings
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Admin Roster Visibility Card */}
              <Card className="border-0 shadow-lg" data-testid="admin-roster-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="font-['Manrope']">Admin Roster Visibility</CardTitle>
                      <CardDescription>Manage your own roster appearance</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="w-5 h-5 text-indigo-600" />
                      <div>
                        <p className="font-medium">Show Me on Roster</p>
                        <p className="text-sm text-slate-500">Appear on the company roster like other employees</p>
                      </div>
                    </div>
                    <Switch
                      checked={adminOnRoster}
                      onCheckedChange={handleToggleAdminRoster}
                      disabled={savingAdminRoster}
                      data-testid="admin-roster-toggle"
                    />
                  </div>
                  <p className="text-xs text-slate-500 px-4">
                    When enabled, you'll appear in the roster builder and can have shifts assigned like employees.
                  </p>
                </CardContent>
              </Card>

              {/* Roster Notification Settings Card */}
              <Card className="border-0 shadow-lg" data-testid="roster-settings-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <CardTitle className="font-['Manrope']">Roster Notification Settings</CardTitle>
                      <CardDescription>Get notified when next week's roster is incomplete</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Notifications Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-teal-600" />
                      <div>
                        <p className="font-medium">Incomplete Roster Notifications</p>
                        <p className="text-sm text-slate-500">Send alerts when next week's roster isn't complete</p>
                      </div>
                    </div>
                    <Switch
                      checked={rosterSettings.incomplete_roster_notification}
                      onCheckedChange={(checked) => setRosterSettings(prev => ({...prev, incomplete_roster_notification: checked}))}
                      data-testid="roster-notification-toggle"
                    />
                  </div>

                  {rosterSettings.incomplete_roster_notification && (
                    <div className="space-y-4 pl-4 border-l-2 border-teal-200">
                      {/* Notification Day */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Notification Day</Label>
                        <Select
                          value={rosterSettings.notification_day}
                          onValueChange={(value) => setRosterSettings(prev => ({...prev, notification_day: value}))}
                        >
                          <SelectTrigger className="w-full" data-testid="roster-notification-day">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sunday">Sunday</SelectItem>
                            <SelectItem value="saturday">Saturday</SelectItem>
                            <SelectItem value="friday">Friday</SelectItem>
                            <SelectItem value="thursday">Thursday</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">Day to check if roster is complete for next week</p>
                      </div>

                      {/* Who to Notify */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Who to Notify</Label>
                        
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-slate-600" />
                            <span className="text-sm">Company Admins</span>
                          </div>
                          <Switch
                            checked={rosterSettings.notify_admins}
                            onCheckedChange={(checked) => setRosterSettings(prev => ({...prev, notify_admins: checked}))}
                            data-testid="roster-notify-admins"
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-600" />
                            <span className="text-sm">Managers</span>
                          </div>
                          <Switch
                            checked={rosterSettings.notify_managers}
                            onCheckedChange={(checked) => setRosterSettings(prev => ({...prev, notify_managers: checked}))}
                            data-testid="roster-notify-managers"
                          />
                        </div>
                      </div>

                      {/* Info Box */}
                      <div className="p-3 bg-teal-50 rounded-lg text-sm text-teal-700">
                        <p><strong>How it works:</strong> Every {rosterSettings.notification_day} at 6 PM, the system checks if all employees have shifts scheduled for the upcoming week. If any employees are missing shifts, notifications are sent.</p>
                      </div>
                      
                      {/* Preview & Test Email Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await axios.post(`${API}/roster/preview-notification-email`);
                              setRosterEmailPreview(res.data);
                              setShowRosterEmailPreview(true);
                            } catch (error) {
                              toast.error('Failed to generate preview');
                            }
                          }}
                          className="flex-1"
                          data-testid="preview-roster-email-btn"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Preview Email
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await axios.post(`${API}/roster/send-test-notification-email`);
                              toast.success(res.data.message);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to send test email');
                            }
                          }}
                          className="flex-1"
                          data-testid="send-test-roster-email-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Send Test Email
                        </Button>
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={handleSaveRoster}
                    className="w-full bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                    disabled={savingSection === 'roster'}
                    data-testid="save-roster-btn"
                  >
                    {savingSection === 'roster' ? (
                      <div className="spinner" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Roster Settings
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="border-0 bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-900">How Settings Work</p>
                      <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                        <li>Break durations define the expected time for each break type</li>
                        <li>Employees see these durations on their break buttons</li>
                        <li>Break warnings notify employees when exceeding the threshold</li>
                        <li>Standard fortnight hours (default 76) are used for overtime calculations</li>
                        <li>Overtime alerts notify admins when employees exceed expected hours</li>
                        <li><strong>Late clock-in alerts</strong> notify admins/managers when rostered employees don't clock in on time</li>
                        <li><strong>Shift swaps</strong> let employees exchange shifts with co-workers approval and manager oversight</li>
                        <li><strong>Roster notifications</strong> alert admins/managers when next week's schedule isn't complete</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Email Preview Dialog */}
      <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              Email Preview
            </DialogTitle>
            <DialogDescription>
              This is how the payroll reminder email will look when sent to you.
            </DialogDescription>
          </DialogHeader>
          
          {emailPreview && (
            <div className="flex flex-col gap-4 overflow-hidden">
              {/* Email Metadata */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-500 w-16">To:</span>
                  <span className="text-slate-700">{emailPreview.recipient_name} &lt;{emailPreview.recipient_email}&gt;</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-500 w-16">Subject:</span>
                  <span className="text-slate-700 font-medium">{emailPreview.subject}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-500 w-16">Period:</span>
                  <span className="text-slate-700">{formatDate(emailPreview.start_date)} - {formatDate(emailPreview.end_date)}</span>
                </div>
              </div>
              
              {/* Email Content Preview */}
              <div className="flex-1 overflow-auto border rounded-lg bg-white">
                <iframe
                  srcDoc={emailPreview.html_content}
                  title="Email Preview"
                  className="w-full h-[500px] border-0"
                  sandbox="allow-same-origin"
                />
              </div>
              
              {/* Info Note */}
              <div className="flex items-center gap-2 text-xs text-slate-500 bg-blue-50 p-3 rounded-lg">
                <Bell className="w-4 h-4 text-blue-500" />
                <span>This email will be automatically sent at 9:00 AM on <strong>{formatDate(emailPreview.end_date)}</strong></span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Roster Email Preview Dialog */}
      <Dialog open={showRosterEmailPreview} onOpenChange={setShowRosterEmailPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-teal-600" />
              Roster Notification Email Preview
            </DialogTitle>
            <DialogDescription>
              This is how the incomplete roster notification email will look when sent to admins.
            </DialogDescription>
          </DialogHeader>
          
          {rosterEmailPreview && (
            <div className="flex flex-col gap-4 overflow-hidden">
              {/* Email Metadata */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-500 w-20">Subject:</span>
                  <span className="text-slate-700 font-medium">{rosterEmailPreview.subject}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-500 w-20">Week:</span>
                  <span className="text-slate-700">{rosterEmailPreview.preview_data?.week_range}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-500 w-20">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    rosterEmailPreview.preview_data?.incomplete_count > 0 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {rosterEmailPreview.preview_data?.incomplete_count > 0 
                      ? `${rosterEmailPreview.preview_data.incomplete_count} incomplete` 
                      : 'All complete'}
                  </span>
                </div>
              </div>
              
              {/* Email Content Preview */}
              <div className="flex-1 overflow-auto border rounded-lg bg-white">
                <iframe
                  srcDoc={rosterEmailPreview.html}
                  title="Roster Email Preview"
                  className="w-full h-[500px] border-0"
                  sandbox="allow-same-origin"
                />
              </div>
              
              {/* Info Note */}
              <div className="flex items-center gap-2 text-xs text-slate-500 bg-teal-50 p-3 rounded-lg">
                <Bell className="w-4 h-4 text-teal-500" />
                <span>This email is sent automatically every <strong>{rosterSettings.notification_day}</strong> at 6:00 PM if there are incomplete rosters.</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanySettings;
